import React, { useContext, FC, memo } from 'react'
import { ScreenContext } from 'react-components'
import { Slide } from 'pure-react-carousel'
import { PropertyTypeSectionTransport } from '../../../transports/home-page-transport'
import { useTranslation, useLocale } from '../../../hooks/locale'
import {
    Container,
    TitleContainer,
    ListContainer,
    SliderContainer,
    StyledCarouselProvider,
    StyledSlider,
    Cell,
    Item,
    ImageContainer,
    PropertyTypeImage,
    Label,
} from './styles'
import PrevNextControls from '../../common/prev-next-controls'
import { useResponsiveValue } from '../../../hooks/responsive'
import { Title, Subtitle } from '../common/styles'
import { getBasePath, getCarouselReachableSlideCount } from '../../../utils/miscellaneous-utils'

type Props = {
    section: PropertyTypeSectionTransport
    totalOnlineListingsCount?: number | null
}

const breakpoints = [
    { query: '(max-width: 399px)', value: 4.5 },
    { query: '(min-width: 400px) and (max-width: 479px)', value: 5.5 },
    { query: '(min-width: 480px) and (max-width: 519px)', value: 5.5 },
    { query: '(min-width: 520px) and (max-width: 639px)', value: 6.5 },
    { query: '(min-width: 640px) and (max-width: 767px)', value: 8.5 },
    { query: '(min-width: 768px) and (max-width: 900px)', value: 5.5 },
    { query: '(min-width: 901px) and (max-width: 1023px)', value: 6.5 },
    { query: '(min-width: 1024px)', value: 7.5 },
]

const _getPropertyTypeImage = (id: number) => {
    const basePath = `/assets/images/property-types/${id}.jpg?imwidth=150`
    return getBasePath(basePath)
}

const FALLBACK_COUNT = '133.921'

const PropertyTypesSection: FC<Props> = ({ section, totalOnlineListingsCount }) => {
    const { items } = section || { items: [] }
    const { isMobile } = useContext(ScreenContext)
    const { t } = useTranslation()
    const { locale } = useLocale()
    const visibleSlides = useResponsiveValue(breakpoints, 2.5)
    const dragStep = Math.floor(visibleSlides)
    const totalSlides = getCarouselReachableSlideCount(items.length, visibleSlides)

    const formattedCount = totalOnlineListingsCount?.toLocaleString(locale) || FALLBACK_COUNT

    if (!items?.length) {
        return null
    }

    return (
        <Container>
            <TitleContainer>
                <Title>{t('pages.home.propertyTypes.heading')}</Title>
                <Subtitle>{t('pages.home.propertyTypes.subheading', { count: formattedCount })}</Subtitle>
            </TitleContainer>
            <ListContainer>
                <SliderContainer>
                    <StyledCarouselProvider
                        totalSlides={totalSlides}
                        visibleSlides={visibleSlides}
                        naturalSlideWidth={1}
                        naturalSlideHeight={1}
                        isIntrinsicHeight
                        lockOnWindowScroll
                        dragEnabled={isMobile}
                        step={dragStep}
                        dragStep={dragStep}
                    >
                        <PrevNextControls
                            totalObjects={items.length}
                            visibleSlides={visibleSlides}
                            desktopTop="-5rem"
                            desktopRight="1rem"
                            wideTop="-5.5rem"
                            wideRight="1rem"
                        />
                        <StyledSlider $transitionTime={0.2}>
                            {items.map((item, idx) => {
                                return (
                                    <Slide index={idx} key={item.id}>
                                        <Cell href={item.link || '#'}>
                                            <Item>
                                                <ImageContainer>
                                                    <PropertyTypeImage
                                                        src={_getPropertyTypeImage(item.id)}
                                                        loading="lazy"
                                                        alt={item.label}
                                                    />
                                                </ImageContainer>
                                                <Label>{item.label}</Label>
                                            </Item>
                                        </Cell>
                                    </Slide>
                                )
                            })}
                        </StyledSlider>
                    </StyledCarouselProvider>
                </SliderContainer>
            </ListContainer>
        </Container>
    )
}

export default memo(PropertyTypesSection)
